import { Deserializer } from 'jsonapi-serializer';
import EnrolmentCategoryField from '../models/EnrolmentCategoryField';
import createDebug from 'debug';
import settingTypesBasicFields from '../constants/setting-types-basic-fields';
import uiTypes from '../constants/field-ui-type';
import { CategoryFieldGroup } from '../models/CategoryFieldGroup';
import { APIConnector } from './api/APIConnector';
import { genericError } from '../models/GenericError';
import { DynamicFieldDescriptor } from '../models/DynamicFieldDescriptor';
import { Name } from '../models/Name';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { Translator } from '../contexts/translator';
const apiConnector = new APIConnector();
const debug = createDebug('custom-fields-service');

export const getCategoryFieldNames = async (profileId: number): Promise<Name[]> => {
    try {
        const urlBase = 'profileFields?';
        const filterByCategory = 'filter=profile.id==' + profileId;
        const onlyNames = '&fields[profileFields]=trnName,settingType,mdtDefault';
        const includeSettingType = '&include=settingType';
        const sortByName = '&sort=trnName';
        const returnMaxSize = '&page[size]=100';

        const url = urlBase + filterByCategory + onlyNames + includeSettingType + returnMaxSize + sortByName;
        const response = await apiConnector.fetchData(url);

        return new Promise(async (resolve, reject) => {
            try {
                const json = await response.json();
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }
                new Deserializer().deserialize(json, (error, fields) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const categoryNames: Name[] = fields.map((field) => {
                        return { label: field['trn-name'], value: field['setting-type']['id'], value2: field['id'], isDefault: field['mdt-default'] };
                    })

                    resolve(categoryNames);
                })
            }
            catch (exception) {
                return reject({ status: response.status, message: 'Failed to read server response.' })
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Get a list of fields (Custom fields & Default fields) that this category supports.
 * @param profileId - specific category to query for
 */
export async function getCategoryFields(profileId: number): Promise<CategoryFieldGroup[]> {
    try {
        const urlBase = 'profileFields?';
        const filterByCategory = 'filter=profile.id==' + profileId;
        const returnMaxSize = '&page[size]=100';
        const sortByName = '&sort=trnName';
        const url = urlBase + filterByCategory + returnMaxSize + sortByName;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, fields: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });;
                }

                const categoryFields: EnrolmentCategoryField[] = fields.map((s, i): EnrolmentCategoryField => {
                    return {
                        id: s['id'],
                        displayName: s['trn-name'],
                        heading: s['master-detail-group-type-name'],
                        toolTip: s['mdt-tooltip'],
                        columnId: s['setting-type-id'],
                        groupId: s['master-detail-group-type-id'],
                        value: "",
                        fieldTypeId: s['mdt-datatype'],
                        masterDetailTypeId: s['master-detail-type-id'],
                        stNameOriginal: s['st-name-orignal'],
                        systemName: s['st-sysname'],
                        ordinal: s['mdt-ordinal'],
                        isDefault: s['mdt-default'] == null ? false : s['mdt-default'] === 1,
                        isEditable: s['mdt-editable'] == null ? false : s['mdt-editable'] === 1,
                        isRequired: s['mdt-required'] == null ? false : s['mdt-required'] === 1,
                        isVisible: s['mdt-visible'] == null ? false : s['mdt-visible'] === 1,
                        options: s['master-detail-lookups']
                    }
                });

                const settingTypesAsArray = Object.keys(settingTypesBasicFields).map(key => settingTypesBasicFields[key]);

                const groupList = categoryFields.reduce((groups, categoryField, index) => {
                    if (categoryField.groupId) {
                        const categoryGroup = groups.find(group => group.id === categoryField.groupId);
                        // add the field to an existing group.
                        if (categoryGroup) {
                            categoryGroup.fields.push(categoryField);
                            return groups;
                        }
                        // create the required group, add the field.
                        else {
                            const newCategoryGroup = { id: categoryField.groupId, heading: categoryField.heading, fields: [categoryField] };
                            groups.push(newCategoryGroup);
                            return groups;
                        }
                    }
                    // default fields
                    else {
                        groups[0].fields.push(categoryField);
                    }

                    return groups;
                }, [{ id: 0, heading: 'Default', fields: new Array(0) }]) // the first group is the default, therefore we reserve it. Heading does not show to user.
                    .map(group => {
                        group.fields.sort((a, b) =>
                            a.ordinal && b.ordinal
                                ? a.ordinal - b.ordinal
                                : ((settingTypesAsArray.find(settingA => a.columnId === settingA.id)?.ordinal ?? a.ordinal)
                                    - (settingTypesAsArray.find(settingB => b.columnId === settingB.id)?.ordinal ?? b.ordinal)))
                        return group
                    });
                return resolve(groupList);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Get a list of fields (Custom fields) that this preset type supports.
 * @param columnId - specific preset to query for
 */
export async function getPresetFields(columnId: number, filterByOrdinal = true): Promise<DynamicFieldDescriptor[]> {
    try {
        const urlBase = 'element?include=uiType,settingType,elementAttributes&';
        const filterByCategory = 'filter=settingType.id==' + columnId;
        const filterOutHiddenElements = ';elementHidden==0';
        const returnMaxSize = '&page[size]=100';
        const url = urlBase + filterByCategory+filterOutHiddenElements + returnMaxSize;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            new Deserializer().deserialize(json, (err: any, fields: any[]) => {
                if (err) {
                    debug('getPresetFields', err);
                    return reject(err);
                }

                const fieldDescriptors: DynamicFieldDescriptor[] = fields.map((apiElement, i): DynamicFieldDescriptor => {

                    // Filter the list of attributes to exclude those that are meant to be hidden.
                    const attributes = apiElement['element-attributes']
                    ? apiElement['element-attributes'].filter(item => item['ea-hidden'] === 0): [];

                    // Extract the name and value for each attribute
                    const options = attributes.map(elementAttribute => {
                            return { name: elementAttribute['trn-name'], id: elementAttribute['ea-valstring'] }
                        });

                    const hasApiElement = (apiElement['setting-type'] != null);
                    return {
                        id: apiElement['id'],
                        displayName: apiElement['trn-name'],
                        toolTip: apiElement['e-hint'],
                        fieldTypeId: apiElement['ui-type']['id'],
                        ordinal: apiElement['e-ordinal'],
                        isRequired: apiElement['e-required'] === null ? false : apiElement['e-required'],//apiElement['mdt-required'] === 1,
                        systemName: apiElement['e-sysname'],
                        unit: apiElement['e-units'],
                        max: apiElement['e-max'],
                        min: apiElement['e-min'],
                        settingTypeOrdinal: !hasApiElement ? 0 : apiElement['setting-type']['st-ordinal'],
                        settingTypeId: !hasApiElement ? undefined : apiElement['setting-type']['id'],
                        options: options
                    }
                });
                const groupList = fieldDescriptors.sort((a, b) => a.ordinal - b.ordinal);
                return resolve(groupList);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const getMasterDetailGroupTypes = async (): Promise<Name[]> => {
    try {
        const urlBase = 'masterDetailGroupType';
        const url = urlBase
        const response = await apiConnector.fetchData(url);

        return new Promise(async (resolve, reject) => {
            try {
                const json = await response.json();
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }
                new Deserializer().deserialize(json, (error, fields) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const masterDetailGroupTypes: Name[] = fields.map((masterDetailGroupType) => {
                        return { label: masterDetailGroupType['trn-name'], value: masterDetailGroupType['id'] };
                    })

                    resolve(masterDetailGroupTypes);
                })
            }
            catch (exception) {
                return reject({ status: response.status, message: 'Failed to read server response.' })
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const getUiFieldName = (id: number, text: string, translate: Translator) => {
    const sId = '' + id;
    const typeObj = Object.values(uiTypes).filter(item => item.value === sId);
    let textToShow = '';

    if (typeObj && typeObj.length > 0) {
        textToShow = translate?.byKey(typeObj[0].translationKey) ?? typeObj[0].label;
    }
    else {
        textToShow = text.split(' ').splice(0)
            .map(w => w[0].toUpperCase() + w.substring(1).toLowerCase())
            .join(' ').slice(5).replace('_', ' ');
    }

    return textToShow;
}

export const getUiTypes = async (translate: Translator): Promise<Name[]> => {
    try {
        const urlBase = 'uiType?page[size]=100';
        const url = urlBase
        const response = await apiConnector.fetchData(url);

        return new Promise(async (resolve, reject) => {
            try {
                const json = await response.json();
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
                }
                new Deserializer().deserialize(json, (error, fields) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const uiTypes: Name[] = fields.map((field) => {
                        return {
                            label: getUiFieldName(field['id'], field['ut-name'], translate),
                            value: field['id']
                        };
                    })

                    resolve(uiTypes);
                })
            }
            catch (exception) {
                return reject({ status: response.status, message: 'Failed to read server response.' })
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeCustomField = async (customField, createCustomField = true) => {
    try {
        const newRecord = {
            data: {
                type: "masterDetailType",
                attributes: {
                    mdtDatatype: customField.uiType,
                    mdtDefault: customField.default ? customField.default : 0,
                    mdtEditable: customField.editable ? customField.editable : 1,
                    mdtHidden: customField.hidden ? customField.hidden : 0,
                    // mdtOrdinal: 4,
                    mdtRequired: customField.required,
                    mdtTooltip: customField.tooltip,
                    mdtVisible: customField.enabled,
                    trnName: customField.name,
                    ...(customField.newGroupName && { newGroupName: customField.newGroupName })
                },
                ...(customField.masterDetailGroupType && {
                    relationships: {
                        masterDetailGroupType: {
                            data: {
                                type: "masterDetailGroupType",
                                id: customField.masterDetailGroupType
                            }
                        }
                    }
                })
            }
        };

        const url = `masterDetailType`;
        let response: Response;

        await apiConnector.sendData(url, createCustomField, newRecord)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (createCustomField) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newCustomField) => {
                        if (error) {
                            return reject({ status: response.status, id: customField.id, success: false, message: genericError });
                        }
                        return resolve({ id: newCustomField.id, success: true });
                    })
                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: customField.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: customField.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: customField.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteCustomField = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `masterDetailType/${id}`;
        const customField = {
            data: {
                type: "masterDetailType",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, customField)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                resolve({ id: id, success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
